import { useState, useEffect } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { useDispatch } from 'react-redux'
import VerifiedBadge from '@/components/verified-badge'
import { colors, fontWeights, breakpoints } from '@/styles'
import { FavouriteHeart } from '@/components/icons'
import { isTruthy } from '@/utils'
import { getImageUrl } from '@/utils/image'
import { defaultAvatar } from '@/components/avatar'
import Skeleton from '@/components/skeleton'
import { Button } from '@/components/button'
import formatName from '@/utils/format-name'
import Tooltip from '@/components/custom-tooltip'
import isVerified from '@/utils/is-verified'
import { showImageModal } from '@/components/image-modal'
import useMediaQuery from '@/hooks/use-media-query'
import { toast } from '@/components/toast-notifications'
import { showTenantVerificationModal } from '@/features/Chat/tenant-verification-modal'
import ApplicationDropdownMenu from './application-dropdown-menu'

const compressedSidebar = '991px'

const LeftTopContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const NamePictureContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    flex-direction: row;
  }
`

const RelativeImageWrapper = styled.div`
  position: relative;
  margin-bottom: 24px;
  cursor: pointer;

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    margin-bottom: 0;
    margin-right: 20px;
  }
`

const TenantImage = styled.img`
  height: 187px;
  width: 100%;
  object-fit: cover;
  object-position: top center;
  border-radius: 6px;

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    width: 80px;
    height: 80px;
    border-radius: 16px;
  }
`

const HeartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  background: ${colors.background};
  border: 1px solid ${colors.grey80};
  opacity: 0.8;
  border-radius: 50%;
  padding: 8px;
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;
  :hover {
    opacity: 1;
  }
  > svg {
    pointer-events: none;
    > path {
      transition: fill 0.2s ease-in-out;
      stroke-width: 2;
      fill: ${props => (props.filled ? colors.red56 : 'rgba(0,0,0,0)')};
    }
  }
`

const MobileHeartWrapper = styled(HeartWrapper)`
  pointer-events: none;
  width: 24px;
  padding: 0;
  background: none;
  border: none;
  position: relative;
  top: unset;
  right: unset;
`

const NameMenuContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
`

const NameText = styled.h1`
  font-size: 24px;
  font-weight: ${fontWeights.bold};
  line-height: 25px;
  max-width: 85%;
  margin: 0px;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media screen and (max-width: ${compressedSidebar}) {
    font-size: 20px;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 20px;
    line-height: 22px;
  }

  @media print {
    white-space: unset;
  }
`

const ApplicationDateText = styled.span`
  font-size: 14px;
  margin-top: 4px;
  font-weight: ${fontWeights.medium};
  color: ${colors.grey48};

  @media screen and (max-width: ${compressedSidebar}) {
    font-size: 12px;
  }
`

const NameApplicationContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const MobileButtonContainer = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 1fr 1fr;
  justify-content: flex-start;
  margin-top: 20px;
`

const VerifiedBadgeWrapper = styled.span`
  cursor: pointer;
`

const RefDiv = styled.div``

const ApplicantInfo = ({
  data = {},
  applicationDate,
  showFullName = true,
  timelineRoom,
  getContractButton,
  isFavourite = false,
  applicationId,
  handleCopyApplicant,
  buttonRef,
  application,
  applicationLoading
}) => {
  const { t } = useTranslation()
  const getImage = (url, options) => (url ? getImageUrl(url, options) : defaultAvatar)
  const dispatch = useDispatch()

  const handleFavourite = async () => {
    if (isTruthy(isFavourite)) {
      const { response } = await dispatch.application.unfavourite(applicationId)
      if (response.ok) {
        toast(
          t(
            'l.application.view.favourites_success_text.text',
            'To view your favourite applicants, please go to the applications page for this listing.'
          ),
          {
            title: `${data?.preferred_name} ${t(
              'l.application.view.remove_from_favourites_success.title',
              'removed from favourites'
            )}`,
            type: 'success'
          }
        )
      }
    } else {
      const { response } = await dispatch.application.favourite(applicationId)
      if (response.ok) {
        toast(
          t(
            'l.application.view.favourites_success_text.text',
            'To view your favourite applicants, please go to the applications page for this listing.'
          ),
          {
            title: `${data?.preferred_name} ${t(
              'l.application.view.add_to_favourites_success.title',
              'added to favourites'
            )}`,
            type: 'success'
          }
        )
      }
    }
  }

  const formattedDate = moment(applicationDate).format('MM-DD-YYYY')
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const history = useHistory()
  const imageSrc = getImage(data?.avatar?.aws_s3_key, {
    width: 240,
    height: 240
  })

  const [scrollTop, setScrollTop] = useState(0)

  useEffect(() => {
    const onScroll = e => {
      setScrollTop(e.target.documentElement.scrollTop)
    }
    window.addEventListener('scroll', onScroll)

    return () => window.removeEventListener('scroll', onScroll)
  }, [scrollTop])

  const formatFullName = data => {
    const end =
      data.preferred_name && data.preferred_name !== data.first_name
        ? ` (${data.preferred_name})`
        : ''
    return isVerified(data, null) && data.first_name.length + data.last_name.length >= 13 ? (
      <span>
        {`${data.first_name}`} <br /> {`${data.last_name}${end}`}
      </span>
    ) : (
      `${data.first_name} ${data.last_name}${end}`
    )
  }

  return (
    <LeftTopContainer>
      <NamePictureContainer>
        <RelativeImageWrapper onClick={() => showImageModal({ src: imageSrc })}>
          {applicationLoading ? (
            <Skeleton width={isMobile ? 80 : '100%'} height={isMobile ? 80 : 187} />
          ) : (
            <TenantImage src={imageSrc} />
          )}
          {!isMobile && !applicationLoading && (
            <Tooltip
              text={
                isFavourite
                  ? t('b.chat.view.remove_from_favourites.button', 'Remove from favourites')
                  : t('b.chat.view.add_to_favourites.button', 'Add to favourites')
              }
              placement="right"
            >
              <HeartWrapper
                id="application-trust-score-sidebar-add-fav"
                filled={isFavourite}
                onClick={e => {
                  e.stopPropagation()
                  handleFavourite()
                }}
              >
                <FavouriteHeart />
              </HeartWrapper>
            </Tooltip>
          )}
        </RelativeImageWrapper>
        <NameApplicationContainer>
          <NameMenuContainer>
            {applicationLoading ? (
              <Skeleton width="80%" height={20} />
            ) : (
              <NameText>
                {!showFullName ? formatName(data) : formatFullName(data)}
                <VerifiedBadgeWrapper
                  onClick={() =>
                    showTenantVerificationModal({
                      verificationStatus: 'verified',
                      timelineRoomId: null
                    })
                  }
                >
                  <VerifiedBadge
                    type="id"
                    width={30}
                    height={26}
                    left={5}
                    bottom={-4}
                    verified={isVerified(data, null)}
                  />
                </VerifiedBadgeWrapper>
              </NameText>
            )}
            {applicationLoading ? (
              <Skeleton width={32} height={32} />
            ) : (
              <RefDiv ref={buttonRef}>
                <ApplicationDropdownMenu
                  handleCopyApplicant={handleCopyApplicant}
                  isFavourite={isFavourite}
                  handleFavourite={handleFavourite}
                  getContractButton={getContractButton}
                  history={history}
                  application={application}
                />
              </RefDiv>
            )}
          </NameMenuContainer>
          {applicationLoading ? (
            <Skeleton width="75%" height={20} />
          ) : (
            <ApplicationDateText>{`${t(
              'l.application.view.applied_on.text',
              'Applied on'
            )}: ${formattedDate}`}</ApplicationDateText>
          )}
        </NameApplicationContainer>
      </NamePictureContainer>

      {applicationLoading ? (
        <Skeleton width={148} height={40} top={20} />
      ) : (
        isMobile && (
          <MobileButtonContainer>
            <Button
              id="application-trust-score-sidebar-add-fav"
              theme="secondary"
              size="small"
              width={'100%'}
              height={40}
              padding="0"
              onClick={handleFavourite}
            >
              <MobileHeartWrapper filled={isFavourite}>
                <FavouriteHeart />
              </MobileHeartWrapper>
              {isFavourite
                ? t('l.application.view.unfavourite.button', 'Unfavourite')
                : t('l.application.view.favourite.button', 'Favourite')}
            </Button>
            {getContractButton()}
          </MobileButtonContainer>
        )
      )}
    </LeftTopContainer>
  )
}

export default ApplicantInfo
